/**
 * The whole audit trail, including events that belong to no project.
 *
 * Sign-ins and refused sign-ins live here and nowhere else, which is what makes this the
 * page an investigation starts from rather than a per-project one.
 */

import { useState } from "react";

import { endpoints } from "@/api/endpoints";
import { Empty, ErrorState, Loading, RelativeTime } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

const INTERESTING = [
  { value: "", label: "All actions" },
  { value: "login", label: "Sign-ins" },
  { value: "login_denied", label: "Refused sign-ins" },
  { value: "password_changed", label: "Password changes" },
  { value: "user_invited", label: "Invitations" },
  { value: "invitation_accepted", label: "Invitations accepted" },
  { value: "user_deactivated", label: "Deactivations" },
  { value: "artifact_downloaded", label: "Downloads" },
  { value: "scan_overridden", label: "Scan overrides" },
  { value: "run_triggered", label: "Runs triggered" },
];

export function PlatformAudit() {
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");

  const { data, error, loading, reload } = useAsync(
    () =>
      endpoints.platformAudit({
        action: action || undefined,
        actor_email: actor || undefined,
        limit: 100,
      }),
    [action, actor],
  );

  if (error) return <ErrorState error={error} onRetry={reload} />;

  const events = data?.items ?? [];

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Audit trail</h1>
          <div className="page-header__meta">
            Append-only, and it outlives the data it describes.
          </div>
        </div>
      </div>

      <div className="stack">
        <div className="row" style={{ gap: 10 }}>
          <select
            className="select"
            style={{ width: "auto" }}
            value={action}
            aria-label="Filter by action"
            onChange={(event) => setAction(event.target.value)}
          >
            {INTERESTING.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <input
            className="input"
            style={{ maxWidth: 260 }}
            type="search"
            value={actor}
            placeholder="Filter by email"
            aria-label="Filter by actor email"
            onChange={(event) => setActor(event.target.value)}
          />

          <div style={{ flex: 1 }} />
          <span className="muted" style={{ fontSize: 12.5 }}>
            {data?.total ?? 0} events
          </span>
        </div>

        {loading ? (
          <Loading rows={5} label="Loading the audit trail" />
        ) : events.length === 0 ? (
          <div className="card">
            <Empty title="Nothing matches">
              Try a different action, or clear the filters.
            </Empty>
          </div>
        ) : (
          <div className="card">
            <div className="table__scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Who</th>
                    <th>What</th>
                    <th>Why</th>
                    <th>From</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td className="muted">
                        <RelativeTime iso={event.created_at} />
                      </td>
                      <td className="muted truncate" style={{ maxWidth: 190 }}>
                        {event.actor_email ?? <span className="faint">the platform</span>}
                      </td>
                      <td>
                        <span className="mono">{event.action.replace(/_/g, " ")}</span>
                        {event.detail && (
                          <div
                            className="faint truncate"
                            style={{ maxWidth: 320, fontSize: 11.5 }}
                            title={JSON.stringify(event.detail)}
                          >
                            {JSON.stringify(event.detail)}
                          </div>
                        )}
                      </td>
                      <td className="muted truncate" style={{ maxWidth: 220 }}>
                        {event.reason ?? "—"}
                      </td>
                      <td className="faint mono" style={{ fontSize: 11.5 }}>
                        {event.ip_address ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data && data.total > events.length && (
          <div className="faint" style={{ fontSize: 12.5 }}>
            Showing the most recent {events.length} of {data.total}. Narrow the filters to
            see further back.
          </div>
        )}
      </div>
    </>
  );
}
